import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  MonitorSmartphone,
  ClipboardList,
  Pill,
  Clock,
  ShieldPlus,
  Zap,
  Stethoscope,
  HeartPulse,
  Globe,
  BadgeDollarSign,
  BrainCircuit,
  Plane,
  Video,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useAgentSelect } from "@/components/site/AgentSelect";
import { KMD, KMD_FEATURES, KMD_ABOUT, KMD_SERVICES, KMD_STATS, KMD_PLANS, KMD_ADDONS } from "@/data";

const ICONS = { MonitorSmartphone, ClipboardList, Pill, Clock, ShieldPlus, Zap };

const MW_GREEN = "#34d399";

const MW_BUNDLES = [
  {
    name: "Copay Bundle",
    tagline: "Everyday care, covered.",
    badge: "Most Popular",
    features: [
      "$20 primary care copays",
      "Specialist & urgent care copays",
      "3-tier prescription plan",
      "Lab & imaging copays",
      "Major medical event sharing",
      "24/7 virtual urgent care — $0 copay",
    ],
  },
  {
    name: "HSA Bundle",
    tagline: "Tax-smart protection.",
    features: [
      "HSA-qualified membership",
      "100% ACA-compliant preventive coverage",
      "Tax-deductible contribution eligibility",
      "Prescription discount card",
      "Major medical event sharing",
      "24/7 virtual urgent care — $0 copay",
    ],
  },
  {
    name: "Concierge Bundle",
    tagline: "Guided, white-glove care.",
    features: [
      "Concierge-guided care team",
      "$2,500 annual preventive benefit per member",
      "Transparent upfront procedure pricing",
      "Major medical event sharing",
      "24/7 virtual urgent care — $0 copay",
    ],
  },
];

const MW_FEATURES = [
  { icon: Video, title: "24/7/365 virtual urgent care", desc: "Talk to licensed, board-certified physicians anytime through Lyric Virtual Urgent Care — always a $0 copay." },
  { icon: Globe, title: "Nationwide open network", desc: "No narrow networks. Choose your own doctors, specialists, and hospitals anywhere in the country." },
  { icon: BadgeDollarSign, title: "Transparent, upfront pricing", desc: "Know what procedures cost before you say yes — no surprise bills, no games." },
  { icon: BrainCircuit, title: "Recuro virtual mental health", desc: "Optional add-on for virtual mental health support, medication management, and acute medication access." },
  { icon: Plane, title: "Living Benefit options", desc: "Funds for the non-medical side of major medical events — travel, lodging, and time off work." },
  { icon: HeartPulse, title: "Flexible Member Responsibility", desc: "Pick the Initial Unshareable Amount that fits your budget — $1,500, $2,500, or $5,000." },
];

const MWCta = ({ name }) => {
  const { openAgentSelect } = useAgentSelect();
  return (
    <button
      onClick={openAgentSelect}
      data-testid={`mightywell-cta-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="mt-7 inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 font-body text-sm font-semibold tracking-wide text-black transition-transform duration-300 hover:scale-[1.03]"
      style={{ backgroundColor: MW_GREEN }}
    >
      Get This Bundle <ArrowUpRight size={15} />
    </button>
  );
};

const KMDButton = ({ children, testid, className = "" }) => {
  const { openAgentSelect } = useAgentSelect();
  return (
    <button
      onClick={openAgentSelect}
      data-testid={testid}
      className={`btn-sweep group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(17,115,185,0.7)] transition-transform duration-300 hover:scale-[1.03] ${className}`}
      style={{ backgroundColor: KMD.blue }}
    >
      {children}
      <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </button>
  );
};

const PlanCta = ({ name }) => {
  const { openAgentSelect } = useAgentSelect();
  return (
    <button
      onClick={openAgentSelect}
      data-testid={`kmd-plan-cta-${name.toLowerCase()}`}
      className="mt-7 inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
      style={{ backgroundColor: KMD.blue }}
    >
      Select Plan <ArrowUpRight size={15} />
    </button>
  );
};

export default function KonnectMDPage() {
  return (
    <>
      {/* HERO — Healthcare hub */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden" data-testid="healthcare-hero">
        <div
          className="absolute -top-1/4 left-1/3 -translate-x-1/2 h-[60vh] w-[60vh] rounded-full blur-[150px]"
          style={{ backgroundColor: "rgba(17,115,185,0.22)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9 }}
            className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]"
          >
            Heritage Healthcare · Every Program We Offer
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-serif-display text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight text-white leading-[0.95] max-w-4xl"
            data-testid="healthcare-hero-heading"
          >
            Healthcare <span className="italic text-gold-gradient">solutions.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mt-6 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65"
          >
            Real healthcare doesn't have to mean traditional insurance. Heritage brings two powerful programs together —
            KonnectMD virtual care memberships for everyday medicine, and MightyWELL Health bundles for everyday copays
            plus major medical protection — with home medication delivery coming soon.
          </motion.p>

          {/* Program quick-jump cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.9 }}
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5"
          >
            <a href="#konnectmd" data-testid="healthcare-jump-kmd" className="group rounded-2xl border border-white/10 bg-[#0a192f] p-6 hover:border-[#1173b9]/60 transition-colors duration-500">
              <div className="inline-flex items-center rounded-lg bg-white px-3 py-2">
                <img src={KMD.logo} alt="KonnectMD" className="h-6 w-auto object-contain" />
              </div>
              <p className="mt-4 font-serif-display text-xl text-white">KonnectMD Virtual Care</p>
              <p className="mt-1.5 font-body text-sm text-white/55">24/7 doctors, free prescriptions, $0 copays — from $59.99/mo covering your whole household.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-body text-sm font-semibold" style={{ color: KMD.lightBlue }}>See memberships <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
            </a>
            <a href="#mightywell" data-testid="healthcare-jump-mw" className="group rounded-2xl border border-white/10 bg-[#0a192f] p-6 hover:border-[#10b981]/60 transition-colors duration-500">
              <div className="inline-flex items-center rounded-lg bg-white px-3 py-2">
                <img src="/partners/mightywell-logo.webp" alt="MightyWELL Health" data-testid="mightywell-jump-logo" className="h-6 w-auto object-contain" />
              </div>
              <p className="mt-4 font-serif-display text-xl text-white">Copay, HSA &amp; Concierge Bundles</p>
              <p className="mt-1.5 font-body text-sm text-white/55">Everyday copays plus major medical event protection through modern health sharing.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-body text-sm font-semibold" style={{ color: MW_GREEN }}>See bundles <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
            </a>
            <div data-testid="healthcare-jump-meds" className="rounded-2xl border border-dashed border-white/15 bg-[#081226] p-6">
              <span className="inline-flex items-center rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-3 py-1 font-body text-[10px] font-bold tracking-[0.25em] uppercase text-[#d4af37]">Coming Soon</span>
              <p className="mt-4 font-serif-display text-xl text-white">Home Medication Delivery</p>
              <p className="mt-1.5 font-body text-sm text-white/55">Heritage will soon deliver medications straight to your door. Announcement coming right here.</p>
            </div>
          </motion.div>

          {/* BANNER */}
          <Reveal delay={0.2}>
            <img
              src={KMD.banner}
              alt="KonnectMD — Quality Care. Anywhere. Anytime."
              data-testid="kmd-banner"
              className="mt-14 w-full h-auto rounded-2xl border border-white/10 shadow-[0_0_60px_rgba(17,115,185,0.2)]"
            />
          </Reveal>
        </div>
      </section>

      {/* PROGRAM ONE — KONNECTMD */}
      <section id="konnectmd" className="relative pt-20 md:pt-24" data-testid="konnectmd-program-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: KMD.lightBlue }}>Program One · Virtual Care Memberships</p>
            <div className="mt-5 flex flex-wrap items-center gap-5">
              <div className="inline-flex items-center rounded-2xl bg-white px-6 py-4">
                <img src={KMD.logo} alt="KonnectMD" data-testid="kmd-hero-logo" className="h-10 md:h-12 w-auto object-contain" />
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-white" data-testid="kmd-hero-heading">
                Quality care. <span className="italic" style={{ color: KMD.lightBlue }}>Anywhere. Anytime.</span>
              </h2>
            </div>
            <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              Affordable, 24/7 national virtual healthcare with no visit fees and no prescription costs.
              Your health. Your time. Your way.
            </p>
            <div className="mt-7">
              <KMDButton testid="kmd-signup-hero">Enroll in KonnectMD</KMDButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-20 md:py-28 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: KMD.lightBlue }}>Member Benefits</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Healthcare that fits <span className="italic" style={{ color: KMD.lightBlue }}>your busy life.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {KMD_FEATURES.map((f, i) => {
              const Icon = ICONS[f.icon];
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.06}>
                  <div data-testid={`kmd-feature-${i}`} className="h-full rounded-3xl border border-white/10 bg-black/40 p-8 transition-colors duration-500 hover:border-[#1173b9]/50">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl" style={{ border: "1px solid rgba(17,115,185,0.45)", color: KMD.lightBlue }}>
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-5 font-serif-display text-2xl text-white">{f.title}</h3>
                    <p className="mt-2 font-body font-light text-sm md:text-base leading-relaxed text-white/60">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: KMD.lightBlue }}>Memberships</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Premium healthcare — <span className="italic" style={{ color: KMD.lightBlue }}>without insurance.</span>
            </h2>
            <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg text-white/60">
              Every plan includes your partner and children under 26. No copays, no hidden fees, no insurance required.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {KMD_PLANS.map((p, i) => (
              <Reveal key={p.name} delay={(i % 4) * 0.05}>
                <div
                  data-testid={`kmd-plan-${p.name.toLowerCase()}`}
                  className="relative h-full flex flex-col rounded-3xl border bg-[#0a192f] p-8 transition-colors duration-500"
                  style={{ borderColor: p.badge ? "rgba(17,115,185,0.55)" : "rgba(255,255,255,0.1)" }}
                >
                  {p.badge && (
                    <span className="absolute top-6 right-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white" style={{ backgroundColor: KMD.blue }}>
                      {p.badge}
                    </span>
                  )}
                  <h3 className="font-serif-display text-3xl text-white">{p.name}</h3>
                  <p className="mt-3">
                    <span className="font-serif-display text-4xl" style={{ color: KMD.lightBlue }}>{p.price}</span>
                    <span className="font-body text-sm text-white/45">/mo</span>
                  </p>
                  <p className="mt-3 font-body font-light text-sm leading-relaxed text-white/55">{p.tagline}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/65">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: KMD.lightBlue }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <PlanCta name={p.name} />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16">
              <h3 className="font-serif-display text-2xl md:text-3xl text-white">Enhance your membership</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {KMD_ADDONS.map((a, i) => (
                  <div key={a.name} data-testid={`kmd-addon-${i}`} className="rounded-2xl border border-white/10 bg-[#0a192f] p-6 transition-colors duration-500 hover:border-[#1173b9]/50">
                    <div className="flex items-baseline justify-between gap-3">
                      <h4 className="font-serif-display text-xl text-white">{a.name}</h4>
                      <span className="font-body text-sm font-semibold shrink-0" style={{ color: KMD.lightBlue }}>{a.price}</span>
                    </div>
                    <p className="mt-2 font-body font-light text-sm leading-relaxed text-white/55">{a.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MIGHTYWELL HEALTH */}
      <section id="mightywell" className="relative py-24 md:py-32 border-y border-white/10" style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.10) 0%, #081226 55%)" }} data-testid="mightywell-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: MW_GREEN }}>Program Two · MightyWell Health</p>
            <div className="mt-5 inline-flex items-center rounded-2xl bg-white px-6 py-4">
              <img src="/partners/mightywell-logo.webp" alt="MightyWELL Health" data-testid="mightywell-section-logo" className="h-10 md:h-12 w-auto object-contain" />
            </div>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
              Major medical protection — <span className="italic" style={{ color: MW_GREEN }}>the MightyWELL way.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              MightyWELL Health bundles everyday care with protection for life's big medical events — through modern
              health sharing, not traditional insurance. Every membership includes 24/7/365 virtual urgent care with
              board-certified physicians at a $0 copay, plus a nationwide open network so you keep the doctors and
              hospitals you trust.
            </p>
          </Reveal>

          {/* Bundles */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {MW_BUNDLES.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.06}>
                <div
                  data-testid={`mightywell-bundle-${i}`}
                  className="relative h-full flex flex-col rounded-3xl border bg-[#0a192f] p-8 transition-colors duration-500"
                  style={{ borderColor: b.badge ? "rgba(16,185,129,0.55)" : "rgba(255,255,255,0.1)" }}
                >
                  {b.badge && (
                    <span className="absolute top-6 right-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-black" style={{ backgroundColor: MW_GREEN }}>
                      {b.badge}
                    </span>
                  )}
                  <h3 className="font-serif-display text-2xl md:text-3xl text-white">{b.name}</h3>
                  <p className="mt-1 font-body text-sm italic" style={{ color: MW_GREEN }}>{b.tagline}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {b.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/65">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: MW_GREEN }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <MWCta name={b.name} />
                </div>
              </Reveal>
            ))}
          </div>

          {/* What MightyWell does */}
          <Reveal delay={0.1}>
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {MW_FEATURES.map((f, i) => (
                <div key={f.title} data-testid={`mightywell-feature-${i}`} className="rounded-2xl border border-white/10 bg-black/30 p-6 transition-colors duration-500 hover:border-[#10b981]/50">
                  <f.icon size={22} strokeWidth={1.5} style={{ color: MW_GREEN }} />
                  <h4 className="mt-4 font-serif-display text-xl text-white">{f.title}</h4>
                  <p className="mt-2 font-body font-light text-sm leading-relaxed text-white/55">{f.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <p className="mt-10 font-body text-xs text-white/35 max-w-4xl leading-relaxed">
              Monthly contributions are set at enrollment and vary by household size, the age of the oldest member, and
              your chosen Member Responsibility (IUA of $1,500, $2,500, or $5,000) — for example, roughly $222–$361/mo
              for a member under 40. MightyWELL plans are facilitated through independent health sharing communities and
              are not traditional insurance; the Concierge Bundle is not ACA-compliant. Final pricing and coverage
              details are confirmed during enrollment with your Heritage agent.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ABOUT + SERVICES */}
      <section className="relative py-24 md:py-32 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <Reveal>
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: KMD.lightBlue }}>Why KonnectMD</p>
              <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-white">
                Virtual first. <span className="italic" style={{ color: KMD.lightBlue }}>Family always.</span>
              </h2>
            </Reveal>
            {KMD_ABOUT.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.06}>
                <p className={`mt-5 font-body font-light leading-relaxed ${i === 0 ? "text-white/75 text-lg" : "text-white/55 text-base md:text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.28}>
              <div className="mt-8">
                <KMDButton testid="kmd-signup-about">Enroll in KonnectMD</KMDButton>
              </div>
            </Reveal>
          </div>

          <div className="space-y-5">
            {KMD_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.06 * i}>
                <div data-testid={`kmd-service-${i}`} className="rounded-2xl border border-white/10 bg-[#0a192f] p-6 transition-colors duration-500 hover:border-[#1173b9]/50">
                  <h3 className="font-serif-display text-xl md:text-2xl text-white">{s.title}</h3>
                  <p className="mt-2 font-body font-light text-sm md:text-base leading-relaxed text-white/60">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 md:py-20 bg-[#0a192f] border-y border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10 text-center">
          {KMD_STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-serif-display text-5xl md:text-6xl" style={{ color: KMD.lightBlue }} data-testid={`kmd-stat-${i}`}>
                {s.value}
              </p>
              <p className="mt-2 font-body text-sm tracking-wide text-white/55">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: "linear-gradient(180deg, rgba(17,115,185,0.16) 0%, #081226 100%)" }}>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Stethoscope size={44} className="mx-auto" style={{ color: KMD.lightBlue }} strokeWidth={1.5} />
            <h2 className="mt-6 font-serif-display text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight text-white">
              Your health. Your time. <span className="italic" style={{ color: KMD.lightBlue }}>Your way.</span>
            </h2>
            <p className="mt-6 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
              Skip the waiting room. Enroll in minutes and talk to a board-certified physician today.
            </p>
            <div className="mt-10 flex justify-center">
              <KMDButton testid="kmd-signup-cta">Enroll in KonnectMD</KMDButton>
            </div>
            <p className="mt-8 font-body text-xs text-white/35 max-w-2xl mx-auto leading-relaxed">
              KonnectMD is an independent telehealth service. Heritage Family Protection Agency connects individuals,
              families, and businesses to KonnectMD plans. Services, availability, and savings may vary by plan and state.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
